import { extractDepsOps } from "./index.js";
import { collectWindowOps, groupActivity, distinctKey } from "../../src/build/index.js";
import { describeState, reconstructState } from "../../src/build/reconstruct.js";

export const extract = extractDepsOps;
const RANK = { answered: 3, failed: 2, unrecorded: 1 };
export const informedness = (op) => (RANK[op.outcome] || 0) * 10000 + (op.output?.length || 0);
export const collect = (prompts) => collectWindowOps(prompts, { informedness });

// A package coordinate in a public registry: identical for every tenant, and it
// outlives every session by definition.
export const groupBy = (op) => (op.packages?.length
  ? `${op.ecosystem}/${op.packages[0]}`
  : distinctKey("unnamed package", op));

// A registry version string is platform schema, so the version a tool REPORTED
// accumulates. The version a command ASKED for is an intent and is not state.
export const stateOf = (flat) => reconstructState(flat, {
  target: groupBy,
  at: (op) => op.ts,
  read: (op) => (op.reported ? { version: op.reported } : null),
  change: (op) => (op.kind === "inspect" ? null : `${op.kind}${op.wanted ? ` ${op.wanted}` : ""}`),
});

export const build = (flat) => {
  const recon = stateOf(flat);
  const byTarget = new Map(recon.targets.map((t) => [t.target, t]));
  const built = grouped(flat);
  for (const g of built.groups) {
    const entry = byTarget.get(g.key);
    if (!entry) continue;
    g.state = describeState(entry, { fields: ["version"] });
    g.changes = entry.changes.map((c) => ({ what: c.what, at: c.at, checked: c.checked, stateDiffered: c.stateDiffered }));
    g.unchecked = entry.unchecked;
    if (entry.unchecked) g.labels = [`${entry.unchecked} unverified change${entry.unchecked > 1 ? "s" : ""}`, ...g.labels];
    if (g.state) g.labels = [g.state.split(" — ")[0], ...g.labels];
  }
  return built;
};

const grouped = (flat) => groupActivity(flat, {
  key: groupBy,
  label: (op) => (op.packages?.length ? op.packages[0] : "an unnamed package"),
  seed: () => ({ kinds: {}, outcomes: {}, labels: [], ecosystems: [], asked: [], inverted: 0 }),
  fold: (g, op) => {
    g.kinds[op.kind] = (g.kinds[op.kind] || 0) + 1;
    g.outcomes[op.outcome] = (g.outcomes[op.outcome] || 0) + 1;
    if (op.inverted) g.inverted += 1;
    if (!g.ecosystems.includes(op.ecosystem)) g.ecosystems.push(op.ecosystem);
    if (op.wanted && !g.asked.includes(op.wanted)) g.asked.push(op.wanted);
  },
  finish: (g) => {
    g.labels = [
      // An inverse tree query is usually a symptom: someone is chasing why a
      // dependency they did not choose is in the tree at all.
      g.inverted ? `${g.inverted}× asked what pulls it in` : null,
      ...g.asked.slice(0, 2).map((v) => `asked for ${v}`),
      ...g.ecosystems,
    ].filter(Boolean);
  },
});

export const anchor = () => "package";
export const ignore = ["output", "steps", "checks"];
